/**
 * WordPress Embeddable ROI Calculator Widget
 * Entry point for the embeddable JavaScript SDK
 */

import React from 'react'
import { createRoot } from 'react-dom/client'
import ROICalculator from './components/ROICalculator'
import './styles/embed.css'

// Widget configuration interface
interface WidgetConfig {
  apiEndpoint: string
  theme?: string
  debug?: boolean
  autosuggest?: {
    enabled: boolean
    debounceMs: number
    minCharacters: number
    maxSuggestions: number
  }
}

// Global widget class
class ROICalculatorWidget {
  private static instances: Map<string, ROICalculatorWidget> = new Map()
  private container: HTMLElement
  private root: any
  private config: WidgetConfig

  constructor(containerId: string, config: WidgetConfig) {
    const container = document.getElementById(containerId)
    if (!container) {
      throw new Error(`Container element with ID "${containerId}" not found`)
    }

    this.container = container
    this.config = config
    this.init()

    // Store instance for debugging
    ROICalculatorWidget.instances.set(containerId, this)
  }

  private init() {
    try {
      // Create React root
      this.root = createRoot(this.container)

      // Apply theme class
      if (this.config.theme) {
        this.container.classList.add(`roi-calculator-theme-${this.config.theme}`)
      }

      // Render the calculator
      this.render()

      if (this.config.debug) {
        console.log('ROI Calculator Widget initialized:', {
          containerId: this.container.id,
          config: this.config
        })
      }
    } catch (error) {
      this.handleError(error as Error)
    }
  }

  private render() {
    // Configure city search service with the API endpoint
    const citySearchApiEndpoint = this.config.apiEndpoint.replace('/calculate', '');

    this.root.render(
      <React.StrictMode>
        <ROICalculator
          apiEndpoint={this.config.apiEndpoint}
          citySearchApiEndpoint={citySearchApiEndpoint}
          className="roi-calculator-embedded"
          enableAutosuggest={this.config.autosuggest?.enabled ?? true}
          debounceMs={this.config.autosuggest?.debounceMs ?? 300}
          minCharacters={this.config.autosuggest?.minCharacters ?? 3}
          maxSuggestions={this.config.autosuggest?.maxSuggestions ?? 10}
        />
      </React.StrictMode>
    )
  }

  private handleError(error: Error) {
    console.error('ROI Calculator Widget Error:', error)

    this.container.innerHTML = `
      <div class="roi-calculator-error">
        <h3>Calculator Error</h3>
        <p>Failed to initialize ROI Calculator: ${error.message}</p>
        <p>Please check the console for more details.</p>
      </div>
    `
  }

  // Public methods
  public destroy() {
    if (this.root) {
      this.root.unmount()
    }
    ROICalculatorWidget.instances.delete(this.container.id)
  }

  public updateConfig(newConfig: Partial<WidgetConfig>) {
    this.config = { ...this.config, ...newConfig }
    this.render()
  }

  // Static methods for global access
  static getInstance(containerId: string): ROICalculatorWidget | undefined {
    return ROICalculatorWidget.instances.get(containerId)
  }

  static getAllInstances(): ROICalculatorWidget[] {
    return Array.from(ROICalculatorWidget.instances.values())
  }

  static destroyAll() {
    ROICalculatorWidget.instances.forEach(instance => instance.destroy())
    ROICalculatorWidget.instances.clear()
  }
}

// Auto-initialization function
function autoInitialize() {
  // Find all calculator containers
  const containers = document.querySelectorAll('.roi-calculator-container')

  containers.forEach((container) => {
    const element = container as HTMLElement
    const configData = element.getAttribute('data-config')

    if (!configData) {
      console.warn('ROI Calculator: No configuration found for container', element.id)
      return
    }

    try {
      const config: WidgetConfig = JSON.parse(configData)

      if (!config.apiEndpoint) {
        throw new Error('API endpoint is required')
      }

      // Initialize widget
      new ROICalculatorWidget(element.id, config)

    } catch (error) {
      console.error('ROI Calculator: Failed to parse configuration', error)
      element.innerHTML = `
        <div class="roi-calculator-error">
          <p>Configuration error: ${(error as Error).message}</p>
        </div>
      `
    }
  })
}

// WordPress integration functions
function initializeWordPressWidget() {
  // Check if we're in WordPress environment
  if (typeof window !== 'undefined' && (window as any).roiCalculatorWP) {
    const wpData = (window as any).roiCalculatorWP

    // Set up global error handler
    window.addEventListener('error', (event) => {
      if (event.filename?.includes('calculator-widget')) {
        console.error('ROI Calculator Widget Error:', event.error)
      }
    })

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', autoInitialize)
    } else {
      autoInitialize()
    }

    // Expose debugging information
    if (wpData.debug) {
      (window as any).ROICalculatorDebug = {
        version: '1.0.0',
        instances: ROICalculatorWidget.getAllInstances(),
        config: wpData,
        reinitialize: autoInitialize,
        destroyAll: ROICalculatorWidget.destroyAll
      }
    }
  } else {
    // Fallback for non-WordPress environments
    autoInitialize()
  }
}

// Global API for manual initialization
const ROICalculatorAPI = {
  init: (containerId: string, config: WidgetConfig) => {
    return new ROICalculatorWidget(containerId, config)
  },

  getInstance: ROICalculatorWidget.getInstance,
  getAllInstances: ROICalculatorWidget.getAllInstances,
  destroyAll: ROICalculatorWidget.destroyAll,

  // Utility functions
  isSupported: () => {
    return !!(
      window.React ||
      (document.createElement('div').attachShadow !== undefined &&
        window.customElements)
    )
  },

  version: '1.0.0'
}

// Expose global API
if (typeof window !== 'undefined') {
  (window as any).ROICalculator = ROICalculatorAPI
}

// Auto-initialize
initializeWordPressWidget()

// Export for module systems
export default ROICalculatorAPI
export { ROICalculatorWidget, type WidgetConfig }